import { useState, useRef, useEffect, useMemo } from 'react';
import { ChevronDown, Search, X } from 'lucide-react';
import { 
  getCountries, 
  getCountryCallingCode, 
  parsePhoneNumber, 
  type CountryCode,
  type E164Number 
} from 'react-phone-number-input';
import { cn } from '@/lib/utils';

interface PhoneInputProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  className?: string;
  disabled?: boolean;
  required?: boolean;
  defaultCountry?: CountryCode;
}

interface CountryOption {
  code: CountryCode;
  name: string;
  callingCode: string;
  isEuropean: boolean;
}

// European country codes (ISO 3166-1 alpha-2)
const EUROPEAN_COUNTRIES: Set<CountryCode> = new Set([
  'NL', 'BE', 'DE', 'FR', 'GB', 'IT', 'ES', 'PT', 'AT', 'CH', 'SE', 'NO', 'DK', 'FI', 'PL',
  'CZ', 'HU', 'RO', 'GR', 'IE', 'IS', 'LU', 'MT', 'CY', 'EE', 'LV', 'LT', 'SI', 'SK', 'BG',
  'HR', 'RS', 'BA', 'MK', 'AL', 'ME', 'XK', 'MD', 'UA', 'BY', 'RU', 'TR'
] as CountryCode[]);

export function PhoneInput({
  value,
  onChange,
  placeholder = 'Enter phone number',
  className = '',
  disabled = false,
  required = false,
  defaultCountry = 'NL', // Default to Netherlands
}: PhoneInputProps) {
  const [isCountryOpen, setIsCountryOpen] = useState(false);
  const [countrySearch, setCountrySearch] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [selectedCountry, setSelectedCountry] = useState<CountryCode>(defaultCountry);
  const containerRef = useRef<HTMLDivElement>(null);
  const countryInputRef = useRef<HTMLInputElement>(null);
  const phoneInputRef = useRef<HTMLInputElement>(null);
  const isInternalChangeRef = useRef(false);
  
  // Update selected country when defaultCountry prop changes (if no value is set)
  useEffect(() => {
    if (!value && selectedCountry !== defaultCountry) {
      setSelectedCountry(defaultCountry);
    }
  }, [defaultCountry, value, selectedCountry]);

  // Parse current value to get country and number
  const parsedPhone = useMemo(() => {
    if (!value) return null;
    try {
      return parsePhoneNumber(value as E164Number);
    } catch {
      return null;
    }
  }, [value]);

  const selectedCallingCode = getCountryCallingCode(selectedCountry);

  // Initialize phone number and country from value (only when value changes externally)
  useEffect(() => {
    // Skip if this is an internal change we just made
    if (isInternalChangeRef.current) {
      isInternalChangeRef.current = false;
      return;
    }

    if (parsedPhone) {
      const nationalNumber = parsedPhone.nationalNumber || '';
      if (nationalNumber !== phoneNumber) {
        setPhoneNumber(nationalNumber);
      }
      if (parsedPhone.country && parsedPhone.country !== selectedCountry) {
        setSelectedCountry(parsedPhone.country);
      }
    } else if (value && value.startsWith('+')) {
      // If value exists but can't be parsed, try to extract country and number
      const match = value.match(/^\+(\d+)(.*)/);
      if (match) {
        const callingCode = match[1];
        const numberPart = match[2].replace(/\D/g, '');
        if (numberPart !== phoneNumber) {
          setPhoneNumber(numberPart);
        }
        
        // Try to find country by calling code
        const country = getCountries().find((code) => getCountryCallingCode(code) === callingCode);
        if (country && country !== selectedCountry) {
          setSelectedCountry(country);
        }
      } else if (!phoneNumber) {
        setPhoneNumber('');
      }
    } else if (!value && phoneNumber) {
      setPhoneNumber('');
    } else if (!value && !phoneNumber && selectedCountry !== defaultCountry) {
      // If no value and no phone number, reset to default country
      setSelectedCountry(defaultCountry);
    }
  }, [value, parsedPhone, defaultCountry, phoneNumber, selectedCountry]);

  // Get all countries with their names and calling codes, sorted with European countries first
  const countries: CountryOption[] = useMemo(() => {
    const allCountries = getCountries().map((code) => ({
      code,
      name: new Intl.DisplayNames(['en'], { type: 'region' }).of(code) || code,
      callingCode: `+${getCountryCallingCode(code)}`,
      isEuropean: EUROPEAN_COUNTRIES.has(code),
    }));
    
    // Sort: European countries first, then alphabetically by name
    return allCountries.sort((a, b) => {
      if (a.isEuropean && !b.isEuropean) return -1;
      if (!a.isEuropean && b.isEuropean) return 1;
      return a.name.localeCompare(b.name);
    });
  }, []);

  // Filter countries based on search
  const filteredCountries = useMemo(() => {
    if (!countrySearch) {
      // When no search, show European countries first (up to 20)
      const european = countries.filter(c => c.isEuropean).slice(0, 15);
      const others = countries.filter(c => !c.isEuropean).slice(0, 5);
      return [...european, ...others];
    }
    
    const searchLower = countrySearch.toLowerCase();
    return countries.filter(
      (country) =>
        country.name.toLowerCase().includes(searchLower) ||
        country.code.toLowerCase().includes(searchLower) ||
        country.callingCode.includes(searchLower)
    );
  }, [countries, countrySearch]);

  // Close dropdown on click outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsCountryOpen(false);
        setCountrySearch('');
      }
    };

    if (isCountryOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isCountryOpen]);

  // Focus search input when opening
  useEffect(() => {
    if (isCountryOpen && countryInputRef.current) {
      countryInputRef.current.focus();
    }
  }, [isCountryOpen]);

  const handleCountrySelect = (countryCode: CountryCode) => {
    setSelectedCountry(countryCode);
    const callingCode = getCountryCallingCode(countryCode);
    isInternalChangeRef.current = true;
    
    // If there's a phone number, reconstruct the full number with new country
    if (phoneNumber) {
      const newValue = `+${callingCode}${phoneNumber}`;
      onChange(newValue);
    } else {
      // Just update the country, keep empty number
      onChange(`+${callingCode}`);
    }
    setIsCountryOpen(false);
    setCountrySearch('');
    phoneInputRef.current?.focus();
  };

  const handlePhoneNumberChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const input = e.target.value.replace(/\D/g, ''); // Remove non-digits
    setPhoneNumber(input);
    isInternalChangeRef.current = true;
    
    // Update the full phone number in E.164 format
    if (input) {
      const fullNumber = `+${selectedCallingCode}${input}`;
      // Try to parse and format it properly
      try {
        const parsed = parsePhoneNumber(fullNumber);
        onChange(parsed.number as string);
      } catch {
        // If parsing fails (incomplete number), just use the raw E.164 format
        onChange(fullNumber);
      }
    } else {
      onChange(`+${selectedCallingCode}`);
    }
  };

  const selectedCountryName = countries.find((c) => c.code === selectedCountry)?.name || selectedCountry;

  return (
    <div ref={containerRef} className={cn('flex rounded-md border border-input bg-background', className)}>
      {/* Country Selector */}
      <div className="relative">
        <button
          type="button"
          onClick={() => !disabled && setIsCountryOpen(!isCountryOpen)}
          disabled={disabled}
          className={cn(
            'flex items-center gap-2 rounded-l-md border-r border-input bg-background px-3 py-2 text-sm',
            'hover:bg-accent/50 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2',
            disabled && 'cursor-not-allowed opacity-50',
            isCountryOpen && 'ring-2 ring-ring ring-offset-2'
          )}
        >
          <span className="whitespace-nowrap">+{selectedCallingCode}</span>
          <ChevronDown className={cn('h-4 w-4 transition-transform', isCountryOpen && 'rotate-180')} />
        </button>

        {/* Country Dropdown */}
        {isCountryOpen && (
          <div className="absolute left-0 z-50 mt-1 w-64 rounded-md border bg-popover shadow-lg">
            {/* Search input */}
            <div className="border-b p-2">
              <div className="relative">
                <Search className="absolute left-2 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <input
                  ref={countryInputRef}
                  type="text"
                  value={countrySearch}
                  onChange={(e) => setCountrySearch(e.target.value)}
                  placeholder="Search countries..."
                  className="w-full rounded-md border-0 bg-transparent py-1.5 pl-8 pr-8 text-sm focus:outline-none"
                />
                {countrySearch && (
                  <button
                    type="button"
                    onClick={() => setCountrySearch('')}
                    className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                  >
                    <X className="h-4 w-4" />
                  </button>
                )}
              </div>
            </div>

            {/* Countries list */}
            <div className="max-h-60 overflow-y-auto p-1">
              {filteredCountries.length === 0 ? (
                <div className="px-3 py-2 text-center text-sm text-muted-foreground">
                  No countries found
                </div>
              ) : (
                filteredCountries.map((country) => {
                  const isSelected = country.code === selectedCountry;
                  return (
                    <button
                      key={country.code}
                      type="button"
                      onClick={() => handleCountrySelect(country.code)}
                      className={cn(
                        'flex w-full items-center justify-between rounded-md px-3 py-2 text-left text-sm',
                        'hover:bg-accent focus:bg-accent focus:outline-none',
                        isSelected && 'bg-accent/50'
                      )}
                    >
                      <div className="flex items-center gap-2">
                        <span className="font-medium">{country.name}</span>
                        <span className="text-xs text-muted-foreground">{country.callingCode}</span>
                      </div>
                    </button>
                  );
                })
              )}
            </div>
          </div>
        )}
      </div>

      {/* Phone Number Input */}
      <input
        ref={phoneInputRef}
        type="tel"
        value={phoneNumber}
        onChange={handlePhoneNumberChange}
        placeholder={placeholder}
        disabled={disabled}
        required={required}
        className="flex-1 rounded-r-md border-0 bg-transparent px-3 py-2 text-sm focus:outline-none"
      />
    </div>
  );
}
